import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';

const EXPENSE_CATEGORIES = ['Fuel', 'Supplies', 'Labor', 'Equipment', 'Materials', 'Other'];

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: '',
    category: 'Other',
    customerId: '',
    customerName: '',
    jobId: '',
    jobDescription: ''
  });

  useEffect(() => {
    const unsubscribers = [];

    const expensesQuery = query(
      collection(db, 'expenses'),
      orderBy('date', 'desc')
    );
    const expUnsub = onSnapshot(expensesQuery, (snapshot) => {
      setExpenses(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    unsubscribers.push(expUnsub);

    const custUnsub = onSnapshot(collection(db, 'customers'), (snapshot) => {
      setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    unsubscribers.push(custUnsub);

    const jobsQuery = query(
      collection(db, 'jobs'),
      orderBy('scheduledDate', 'desc')
    );
    const jobsUnsub = onSnapshot(jobsQuery, (snapshot) => {
      setJobs(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    unsubscribers.push(jobsUnsub);

    return () => unsubscribers.forEach(u => u());
  }, []);

  const jobsForCustomer = form.customerId
    ? jobs.filter(j => j.customerId === form.customerId)
    : [];

  const resetForm = () => {
    setForm({
      date: new Date().toISOString().split('T')[0],
      description: '',
      amount: '',
      category: 'Other',
      customerId: '',
      customerName: '',
      jobId: '',
      jobDescription: ''
    });
    setEditingExpense(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount.');
      return;
    }
    const customer = customers.find(c => c.id === form.customerId);
    const customerName = customer
      ? (customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim())
      : '';
    const job = jobs.find(j => j.id === form.jobId);
    const jobDescription = job ? `${job.serviceType || ''} ${job.scheduledDate || ''}`.trim() : '';

    const data = {
      date: form.date,
      description: form.description.trim() || 'Expense',
      amount,
      category: form.category,
      customerId: form.customerId || null,
      customerName: customerName || null,
      jobId: form.jobId || null,
      jobDescription: jobDescription || null,
      updatedAt: new Date()
    };

    try {
      if (editingExpense) {
        await updateDoc(doc(db, 'expenses', editingExpense.id), data);
      } else {
        data.createdAt = new Date();
        await addDoc(collection(db, 'expenses'), data);
      }
      resetForm();
    } catch (err) {
      console.error(err);
      alert('Failed to save expense.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await deleteDoc(doc(db, 'expenses', id));
    } catch (err) {
      console.error(err);
      alert('Failed to delete.');
    }
  };

  const startEdit = (exp) => {
    setEditingExpense(exp);
    setForm({
      date: (exp.date || '').slice(0, 10),
      description: exp.description || '',
      amount: String(exp.amount ?? ''),
      category: exp.category || 'Other',
      customerId: exp.customerId || '',
      customerName: exp.customerName || '',
      jobId: exp.jobId || '',
      jobDescription: exp.jobDescription || ''
    });
    setShowForm(true);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const str = typeof d === 'string' ? d.slice(0, 10) : d;
    return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-secondary-900">Expenses</h1>
          <p className="text-secondary-600 text-sm">Track expenses and optionally link to a customer or job.</p>
        </div>
        <button
          type="button"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="btn-primary whitespace-nowrap"
        >
          + Add expense
        </button>
      </div>

      {showForm && (
        <div className="card mb-6">
          <div className="p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-secondary-900 mb-4">
              {editingExpense ? 'Edit expense' : 'New expense'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.amount}
                    onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="input w-full"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                  className="input w-full"
                >
                  {EXPENSE_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  className="input w-full"
                  placeholder="What was this expense for?"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Attach to customer (optional)</label>
                <select
                  value={form.customerId}
                  onChange={(e) => setForm(f => ({ ...f, customerId: e.target.value, jobId: '' }))}
                  className="input w-full"
                >
                  <option value="">— None —</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown'}
                    </option>
                  ))}
                </select>
              </div>
              {form.customerId && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Attach to job (optional)</label>
                  <select
                    value={form.jobId}
                    onChange={(e) => setForm(f => ({ ...f, jobId: e.target.value }))}
                    className="input w-full"
                  >
                    <option value="">— None —</option>
                    {jobsForCustomer.map(j => (
                      <option key={j.id} value={j.id}>
                        {j.serviceType || 'Job'} · {j.scheduledDate ? j.scheduledDate.slice(0, 10) : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">{editingExpense ? 'Update' : 'Add expense'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-secondary-600 uppercase">Date</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-secondary-600 uppercase">Description</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-secondary-600 uppercase">Category</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-secondary-600 uppercase">Customer / Job</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-secondary-600 uppercase">Amount</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-secondary-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-100">
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-secondary-500">
                    No expenses yet. Add one to get started.
                  </td>
                </tr>
              ) : (
                expenses.map((exp) => (
                  <tr key={exp.id} className="hover:bg-secondary-50/50">
                    <td className="px-4 py-2.5 text-sm text-secondary-900 whitespace-nowrap">{formatDate(exp.date)}</td>
                    <td className="px-4 py-2.5 text-sm text-secondary-900">{exp.description || '—'}</td>
                    <td className="px-4 py-2.5 text-sm text-secondary-600">{exp.category || '—'}</td>
                    <td className="px-4 py-2.5 text-sm text-secondary-600">
                      {exp.customerName ? (
                        <span>{exp.customerName}{exp.jobDescription ? ` · ${exp.jobDescription}` : ''}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right font-medium text-red-600">{formatCurrency(exp.amount)}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button type="button" onClick={() => startEdit(exp)} className="text-primary-600 hover:text-primary-700 text-sm font-medium mr-2">Edit</button>
                      <button type="button" onClick={() => handleDelete(exp.id)} className="text-red-600 hover:text-red-700 text-sm font-medium">Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {expenses.length > 0 && (
          <div className="px-4 py-3 border-t border-secondary-200 bg-secondary-50/50 text-sm font-semibold text-secondary-900">
            Total expenses: <span className="text-red-600">{formatCurrency(expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0))}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Expenses;
